import { maxLength, minLength, required, sameAs } from 'vuelidate/lib/validators';
import { Inject, Vue, Component } from 'vue-property-decorator';
import LoginService from '@/account/login.service';
import ResetPasswordService from '@/account/reset-password/reset.password.service';
import {hasNumericalValues,
        hasUpperAndLowerCase,
        isInBlacklist,
        hasPesronalInfo,
        defaulPasswordPolicies,
        buildPasswordHint} from '@/shared/data/data-validation';
import { utils } from '@/shared/utils';

const validations = {
  resetAccount: {
    newPassword: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(254),
    },
    confirmPassword: {
      sameAsPassword: sameAs('newPassword')
    },
  },
};

@Component({
  validations,
})
export default class ResetPasswordFinish extends Vue {
  @Inject('loginService')
  private loginService: () => LoginService;

  @Inject('resetPasswordService')
  private resetPasswordService: () => ResetPasswordService;

  public doNotMatch: string = null;
  public success: string = null;
  public error: string = null;
  public keyMissing: boolean = null;
  public passwordPolicies = null;
  public userInfo = null;
  public key: any;
  public passwordHint = 'Min. 6 characters.';
  public resetAccount: any = {
    newPassword: null,
    confirmPassword: null,
  };
  public uiOptions: any = [];
  public hidden = true;
  public hiddenConfirm = true;
  public dbKeyIsMissing = false;

  created(): void {
    if (this.$route !== undefined && this.$route.query !== undefined && this.$route.query.key !== undefined && this.$route.query.key !== 'null') {
      this.key = this.$route.query.key;
    }
    this.keyMissing = !this.key;

    if (!this.keyMissing) {
      this.resetPasswordService().getUserInfo(this.key)
      .then((user) => {
        this.userInfo = user;
      })
      .catch(() => {
        this.dbKeyIsMissing = true;
      });

      this.resetPasswordService().getPasswordPolicies(this.key)
      .then((res) => {
        this.passwordPolicies = JSON.parse(res.value);
      })
      .catch(() => {
        this.passwordPolicies = defaulPasswordPolicies;
      })
      .finally(() => {
        this.passwordHint = buildPasswordHint(this.passwordPolicies);
        this.laodPasswordOptions();
      });
    } else {
      this.initResetPassword();
    }
  }


  public laodPasswordOptions() {
    for (const property in this.passwordPolicies) {
      if (this.passwordPolicies[property]) {
        this.uiOptions.push({
          name: `${property}`,
          value: this.passwordPolicies[property],
          valid: false
        });
      }
    }
  }


  public finishReset(): void {
    this.doNotMatch = null;
    this.success = null;
    this.error = null;
    if (this.resetAccount.newPassword !== this.resetAccount.confirmPassword) {
      this.doNotMatch = 'ERROR';
    } else {
      this.resetPasswordService().finishReset({ key: this.key, newPassword: this.resetAccount.newPassword })
      .then((response) => {
        if (response) {
          this.success = 'OK';
        } else {
          this.success = null;
          this.error = 'ERROR';
          this.initResetPassword();
        }
      });
    }
  }


  public updateValid(name, valid) {
    const value = this.uiOptions.find((o) => o.name === name);
    if (value) {
      value.valid = valid;
    }
  }

  public reset() {
    this.updateValid('minLen', false);
    this.updateValid('numerical', false);
    this.updateValid('upperLower', false);
    this.updateValid('personalInfo', false);
    this.updateValid('blacklist', false);
  }

  public validate() {
    if (!this.resetAccount.newPassword) {
        this.reset();
    } else {
      if (this.resetAccount.newPassword.length >= this.passwordPolicies.minLen) {
        this.updateValid('minLen', true);
      } else {
        this.updateValid('minLen', false);
      }

      if (this.passwordPolicies.numerical && hasNumericalValues(this.resetAccount.newPassword)) {
        this.updateValid('numerical', true);
      } else {
        this.updateValid('numerical', false);
      }

      if (this.passwordPolicies.upperLower && hasUpperAndLowerCase(this.resetAccount.newPassword)) {
        this.updateValid('upperLower', true);
      } else {
        this.updateValid('upperLower', false);
      }

      if (this.userInfo && this.passwordPolicies.personalInfo && hasPesronalInfo(this.userInfo, this.resetAccount.newPassword)) {
        this.updateValid('personalInfo', true);
      } else {
        this.updateValid('personalInfo', false);
      }

      if (this.passwordPolicies.blacklist && isInBlacklist(this.resetAccount.newPassword)) {
        this.updateValid('blacklist', true);
      } else {
        this.updateValid('blacklist', false);
      }
    }
  }

  public openLogin() {
    this.$router.push({
      name: 'Signin'
    });
  }

  public resetPassword() {
    this.$router.push({
      name: 'ResetPasswordInit'
    });
  }


  public initResetPassword() {
    this.$router.push({
      name: 'ResetPasswordInit',
      query: {'invalidKey': '1'}
    });
  }

  public setPasswordRules() {
    const errors = [];
    if (this.passwordPolicies) {
      if (!this.resetAccount.newPassword) {
        errors.push('The password is required');
      }

      if (this.resetAccount.newPassword) {
        if (this.resetAccount.newPassword.length < this.passwordPolicies.minLen) {
          errors.push('The password must have at least ' + this.passwordPolicies.minLen + ' characters!');
        }

        if (this.passwordPolicies.numerical && !hasNumericalValues(this.resetAccount.newPassword)) {
          errors.push('The password must contain at least one number!');
        }

        if (this.passwordPolicies.upperLower && !hasUpperAndLowerCase(this.resetAccount.newPassword)) {
          errors.push('The password must have at least one upper and lower case letters!');
        }

        if (this.userInfo && this.passwordPolicies.personalInfo && !hasPesronalInfo(this.userInfo, this.resetAccount.newPassword)) {
          errors.push('The password must not have personal information!');
        }

        if (this.passwordPolicies.blacklist && !isInBlacklist(this.resetAccount.newPassword)) {
          errors.push('The password must not be a common password!');
        }
      }
    } else {
      return [
        (v) => !!v || this.$t('global.messages.validate.newpassword.required'),
        (v) => (v && v.length >= 6) || this.$t('global.messages.validate.newpassword.minlength'),
        (v) => (v && v.length <= 50) || this.$t('global.messages.validate.newpassword.maxlength'),
      ];
    }

    return errors;
  }

  public setRepeatPasswordRules() {
    return [
      (v) => !!v || this.$t('global.messages.validate.confirmpassword.sameas'),
      (v) => (v && v.length >= 6) || this.$t('global.messages.validate.confirmpassword.sameas'),
      (v) => (v && v === this.resetAccount.newPassword) || this.$t('global.messages.validate.confirmpassword.sameas'),
    ];
  }

  public showAlert() {
    if (this.success) {
      this.$swal.fire('Saved!', this.$t('reset.finish.messages.success').toString() +
      '<a v-on:click="openLogin">sign in</a>', 'success');
    }

  }

  public get year() {
    return utils.getYear();
  }

  public get isMobile() {
    return this.$vuetify.breakpoint.width < 961;
  }

  public get pageHeight() {
    return this.$vuetify.breakpoint.height;
  }
}
